// Supercategory grouping for the tree — collapses ~74 top-level branches
// into ~11 top-level supercategories. Applied client-side at render time in
// TreePage.jsx; the underlying data in mock.js is untouched.
//
// Order matters — this is the display order. The first four are "meta"
// branches, then content supercategories.

export const SUPERCATEGORIES = [
  {
    name: 'Popular Toolkits',
    matches: ['Popular Toolkits'],
    keepFlat: true, // Don't wrap; this is already curated
  },
  {
    name: 'Discovery',
    matches: ['Discovery'],
    keepFlat: true,
  },
  {
    name: 'Creators & Influencers',
    matches: ['Creators & Influencers'],
    keepFlat: true,
  },
  {
    name: 'Companies',
    matches: ['Companies'],
    keepFlat: true,
  },
  {
    name: 'Creative Studio',
    matches: [
      'Text & Writing',
      'Image Generation',
      'Video Generation',
      'Music & Audio',
      'Speech',
      'Photography',
      'Design',
      '3D Creation',
      'Virtual Humans',
      'Fashion',
      'Creative Media',
      'Virtual Reality & AR',
      'Creative Disciplines',
    ],
  },
  {
    name: 'Build & Develop',
    matches: [
      'Coding',
      'Website Builders',
      'App Builders',
      'Game Development',
      'Automation & Agents',
      'Robotics',
      'Simulation',
      'AI for Physical Products',
      'Architecture & Engineering',
      'Industrial & Manufacturing',
      'Engineering Disciplines',
      'AI Chatbots / LLMs',
      'Prompt Engineering / Learn',
    ],
  },
  {
    name: 'Work by Industry',
    matches: [
      'By Industry',
      'By Profession',
      'By Task / Goal',
      'Marketing',
      'Sales',
      'Business',
      'Finance',
      'Investing & Finance',
      'Real Estate',
      'Real Estate (Full)',
      'Legal',
      'Healthcare',
      'Medicine & Healthcare',
      'Education',
      'Education (Expanded)',
      'Customer Service',
      'Social Media',
      'Government / Public Sector',
    ],
  },
  {
    name: 'Science & Data',
    matches: [
      'Science & Research',
      'Science',
      'Biology & Chemistry',
      'Data & Analytics',
      'Geographic & Mapping',
      'Nature & Outdoors',
      'Humanities & Social Sciences',
      'Research & Learning',
    ],
  },
  {
    name: 'Career & Money',
    matches: [
      'Productivity',
      'Money & Careers',
    ],
  },
  {
    name: 'Everyday Life',
    matches: [
      'Personal / Life Hacks',
      'Lifestyle',
      'Entertainment',
      'Local & Events',
      'Shopping & Deals',
      'Community & Classifieds',
      'Community & News',
      'News',
      'Reviews',
      'Resources',
      'Faith & Religion',
      'International',
    ],
  },
  {
    name: 'Utilities & Meta',
    matches: [
      'Document Processing',
      'Security',
      'Cybersecurity & OSINT',
      'Specialized Generators',
      'AI Detection & Anti-AI',
      'Underground / TikTok-Viral AI',
    ],
  },
];

/**
 * Wrap the flat top-level tree children under the supercategories.
 * Preserves original ids/urls; only changes the visual hierarchy.
 */
export function regroupTree(root) {
  if (!root || !root.children) return root;
  const byName = new Map(root.children.map((c) => [c.name, c]));
  const consumed = new Set();
  const newChildren = [];

  for (const sc of SUPERCATEGORIES) {
    if (sc.keepFlat) {
      for (const m of sc.matches) {
        const found = byName.get(m);
        if (found) {
          newChildren.push(found);
          consumed.add(m);
        }
      }
      continue;
    }
    const children = [];
    for (const m of sc.matches) {
      const found = byName.get(m);
      if (found) {
        children.push(found);
        consumed.add(m);
      }
    }
    if (children.length) {
      newChildren.push({ name: sc.name, children });
    }
  }

  // Anything not consumed — surface under "More" so nothing is silently dropped.
  const leftovers = root.children.filter((c) => !consumed.has(c.name));
  if (leftovers.length) {
    newChildren.push({ name: 'More', children: leftovers });
  }

  return { ...root, children: newChildren };
}
